import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import { shallow } from "zustand/shallow";
import { cn } from "@/lib/utils";
import { useStore, usePayment, useUser, useUserVip } from "@/stores/appStore";
import { MemberLabel } from "@/components/fun-ui/MemberLabel";
import { PriceBadge } from "@/components/site/PriceBadge";
import { BadgeType, PaymentType, type LadderVipPayment } from "@/stores/slices/commonSlice";
import type { VipUpgradePreviewOption } from "@/stores/slices/userSlice";
import {
  ladderUserTierFromVip,
  matchLadderUserTier,
  PAYMENT_OPTION_CARD_MIN_HEIGHT_PX,
  PAYMENT_UPGRADE_CARD_BORDER_PX,
  PAYMENT_UPGRADE_CARD_INNER_RADIUS_PX,
  PAYMENT_UPGRADE_CARD_OUTER_RADIUS_PX,
  PAYMENT_UPGRADE_DISCOUNT_BADGE_INSET_HEIGHT_PX,
  paymentUpgradeCardInnerRadiusPx,
} from "@/lib/payment";
import { UpgradeArrowIcon } from "@/components/fun-ui/icons";
import { PaymentBenefitsCompareTable } from "./PaymentBenefitsCompareTable";
import Loading from "../Loading";
import type { BenefitsCompareVariant } from "./PaymentBenefitsCompareTable";

/** 单月优惠均价与原价（数值）一致时不展示下方一行 */
function sameMonthlyUnitPrice(discounted: number | string, original: number | string): boolean {
  const a = Number(discounted);
  const b = Number(original);
  if (Number.isFinite(a) && Number.isFinite(b)) {
    return Math.abs(a - b) < 1e-6;
  }
  return String(discounted).trim() === String(original).trim();
}

export type PaymentDialogUpgradeProps = {
  scale: number;
  paymentTabType: Extract<PaymentType, 'upgrade' | 'upgradePlus3x'>;
  isActive: boolean;
  activedIndex?: number;
  onActivedIndexChange?: (index: number) => void;
  onPaymentOptionChange: (option: { renewalDays: number, paymentPrice: number }) => void;
  /** 置于左列底部：二维码与支付说明，或超出有效期上限时的说明+开具发票（与右列权益表同宽网格） */
  leftColumnFooter?: React.ReactNode;
}

export const PaymentDialogUpgrade = ({
  scale = 1,
  paymentTabType,
  isActive,
  activedIndex: propActivedIndex,
  onActivedIndexChange,
  onPaymentOptionChange,
  leftColumnFooter,
}: PaymentDialogUpgradeProps) => {
  const [localActivedIndex, setLocalActivedIndex] = useState<number>(0);
  const [hoveredCardIndex, setHoveredCardIndex] = useState<number | null>(null);

  const activedIndex = propActivedIndex ?? localActivedIndex;

  const setActivedIndex = useCallback((index: number) => {
    if (onActivedIndexChange) {
      onActivedIndexChange(index);
    } else {
      setLocalActivedIndex(index);
    }
  }, [onActivedIndexChange]);

  const membershipPlan = paymentTabType === 'upgrade' ? 'plus' : 'plus3x';

  /** Plus 3倍：`paymentUpgradeSetPlus3x` → `/common/config` key `payment_upgrade_set_plus_3x`（见 commonSlice） */
  const { value, fetch, loading: upgradeSetLoading } = useStore(
    (s) =>
      membershipPlan === 'plus'
        ? {
          value: s.paymentUpgradeSet.value,
          fetch: s.paymentUpgradeSet.fetch,
          loading: s.paymentUpgradeSet.loading,
        }
        : {
          value: s.paymentUpgradeSetPlus3x.value,
          fetch: s.paymentUpgradeSetPlus3x.fetch,
          loading: s.paymentUpgradeSetPlus3x.loading,
        },
    shallow,
  );

  const {
    paymentOpen,
    setPaymentPrice,
    getPaymentPrice,
  } = usePayment();

  const { vip, vipType } = useUserVip();
  const {
    fetchVipUpgradePreview,
    vipUpgradePreview,
    vipUpgradePreviewLoading,
  } = useUser();
  const isCurrentPlus3xUser = vipType === 'plus_3x';
  const isCurrentPlusUser = vip === 1 && vipType === 'plus';

  /** Plus 用户打开「Plus 3倍会员」Tab：走 upgradePreview 抵扣与档位过滤 */
  const usePlus3xPreview = paymentTabType === 'upgradePlus3x' && vip === 1 && vipType === 'plus';
  const currentUserTier = ladderUserTierFromVip(vip, vipType);

  useEffect(() => {
    if (!value?.items?.length) {
      fetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipPlan]);

  useEffect(() => {
    if (!isActive || !usePlus3xPreview) return;
    fetchVipUpgradePreview();
  }, [isActive, usePlus3xPreview, fetchVipUpgradePreview]);

  const displayRows = useMemo((): { item: LadderVipPayment; previewOption: VipUpgradePreviewOption | null }[] => {
    const sourceItems = value?.items || [];
    const items = paymentTabType === 'upgradePlus3x'
      ? sourceItems.filter((item) => {
        if (!item.users?.length) {
          return true;
        }
        return matchLadderUserTier(item.users, currentUserTier);
      })
      : sourceItems;
    if (!items.length) return [];
    if (!usePlus3xPreview) {
      return items.map((item) => ({ item, previewOption: null }));
    }
    if (vipUpgradePreviewLoading && !vipUpgradePreview) {
      return [];
    }
    const preview = vipUpgradePreview;
    if (!preview?.upgradeOptions?.length) {
      return items.map((item) => ({ item, previewOption: null }));
    }
    const cred = preview.totalDeduction / 100;
    return items
      .map((item) => ({
        item,
        previewOption:
          preview.upgradeOptions.find(
            (o) => Math.abs(Number(o.price) - Number(item.price)) < 0.02,
          ) ?? null,
      }))
      .filter(({ item, previewOption }) => {
        if (!previewOption) return false;
        if (previewOption.actualAmount <= 0 || item.price < cred) return false;
        if (previewOption.status === 'exceeded') return false;
        return true;
      });
  }, [value?.items, paymentTabType, currentUserTier, usePlus3xPreview, vipUpgradePreview, vipUpgradePreviewLoading]);

  /** 与 createOrder 的 amount 一致：档位原价（元），抵扣由服务端按 preview 处理 */
  const prices = useMemo(() => {
    return displayRows.map((row) => row.item.price);
  }, [displayRows]);

  const paymentPrice = getPaymentPrice(paymentTabType);
  const paymentPriceRef = useRef(paymentPrice);
  const activedIndexRef = useRef(activedIndex);
  const isUpdatingFromPriceRef = useRef(false);
  const hasCalledOnPaymentOptionChangeRef = useRef(false);

  useEffect(() => {
    paymentPriceRef.current = paymentPrice;
  }, [paymentPrice]);

  useEffect(() => {
    activedIndexRef.current = activedIndex;
  }, [activedIndex]);

  useEffect(() => {
    if (!isActive) return;
    if (paymentPrice !== 0 && prices.length > 0 && displayRows.length) {
      const index = prices.findIndex((p) => Math.abs(p - paymentPrice) < 0.005);
      if (index !== -1) {
        const days = displayRows[index]?.item.days || 0;
        if (index !== activedIndexRef.current) {
          isUpdatingFromPriceRef.current = true;
          hasCalledOnPaymentOptionChangeRef.current = false;
          setActivedIndex(index);
        } else if (!hasCalledOnPaymentOptionChangeRef.current) {
          hasCalledOnPaymentOptionChangeRef.current = true;
          onPaymentOptionChange({ renewalDays: days, paymentPrice });
        }
      }
    }
  }, [isActive, paymentPrice, prices, displayRows, onPaymentOptionChange, setActivedIndex]);

  useEffect(() => {
    if (!isActive) return;
    if (typeof activedIndex === 'undefined' || prices.length === 0) {
      return;
    }
    const price = prices[activedIndex];
    if (price === undefined) return;

    const days = displayRows[activedIndex]?.item.days || 0;

    if (isUpdatingFromPriceRef.current) {
      isUpdatingFromPriceRef.current = false;
      hasCalledOnPaymentOptionChangeRef.current = true;
      onPaymentOptionChange({ renewalDays: days, paymentPrice: price });
      return;
    }

    if (Math.abs(paymentPriceRef.current - price) >= 0.005) {
      onPaymentOptionChange({ renewalDays: days, paymentPrice: price });
      setPaymentPrice(price, paymentTabType);
    }
  }, [isActive, prices, activedIndex, displayRows, setPaymentPrice, onPaymentOptionChange, paymentTabType]);

  useEffect(() => {
    if (!isActive) return;
    if (paymentOpen === true && paymentPrice === 0) {
      setActivedIndex(0);
    }
  }, [isActive, paymentPrice, paymentOpen, setActivedIndex]);

  useEffect(() => {
    if (!isActive || !displayRows.length) return;
    if (activedIndex >= displayRows.length) {
      setActivedIndex(0);
    }
  }, [isActive, displayRows.length, activedIndex, setActivedIndex]);

  const compareVariant: BenefitsCompareVariant =
    paymentTabType === 'upgrade' ? 'memberPlus' : 'memberPlus3x';

  const showPriceColumnLoading =
    isActive
    && (
      (upgradeSetLoading && !value?.items?.length)
      || (usePlus3xPreview && vipUpgradePreviewLoading && !vipUpgradePreview)
    );

  const durationNote =
    paymentTabType === 'upgradePlus3x'
      ? 'Plus 3倍会员每月时长为固定 31 天。'
      : 'Plus 会员每月时长为固定 31 天。';
  const subtitleText = paymentTabType === 'upgradePlus3x'
    ? (isCurrentPlusUser
      ? (value?.plus3xSubtitle || value?.title)
      : value?.title)
    : value?.title;

  const upgradeFooterRef = useRef<HTMLDivElement | null>(null);
  const [upgradeFooterH, setUpgradeFooterH] = useState(0);

  useLayoutEffect(() => {
    if (!leftColumnFooter) {
      setUpgradeFooterH(0);
      return;
    }
    const el = upgradeFooterRef.current;
    if (!el) return;
    const sync = () => setUpgradeFooterH(el.offsetHeight);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [leftColumnFooter, scale, isActive, paymentTabType, paymentPrice, displayRows.length]);

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
      <div
        className="mx-auto w-full shrink-0 text-center text-muted-foreground font-normal"
        style={{
          fontSize: `${16 * scale}px`,
          lineHeight: `${22 * scale}px`,
          maxWidth: `${640 * scale}px`,
          paddingLeft: `${16 * scale}px`,
          paddingRight: `${16 * scale}px`,
          marginBottom: `${24 * scale}px`,
        }}
      >
        {subtitleText}
      </div>
      <div className="relative min-h-0 w-full min-w-0 flex-1 overflow-hidden">
        <div
          className="absolute left-0 right-0 top-0 z-0 h-fit"
          style={{
            maxHeight: leftColumnFooter
              ? `calc(100% - ${Math.max(upgradeFooterH, Math.round(96 * scale))}px)`
              : '100%',
          }}
        >
          <div
            className="w-full shrink-0"
            style={{
              paddingLeft: `${32 * scale}px`,
              paddingRight: `${32 * scale}px`,
            }}
          >
            <div
              className="flex w-full min-w-0 flex-row flex-nowrap items-start"
              style={{ gap: `${20 * scale}px` }}
            >
              <div
                className="flex min-w-0 flex-col overflow-x-hidden bg-transparent"
                style={{
                  flex: '466 1 0',
                  minWidth: 0,
                }}
              >
                <div className="flex w-full min-w-0 flex-col">
                  <div className="flex flex-col" style={{ gap: `${10 * scale}px` }}>
                    {
                      showPriceColumnLoading ? (
                        <div
                          className="flex w-full items-center justify-center rounded-xl border border-[#E8E4E0] bg-background"
                          style={{ minHeight: `${PAYMENT_OPTION_CARD_MIN_HEIGHT_PX * scale * 2}px` }}
                        >
                          <Loading mask size="lg" />
                        </div>
                      ) : displayRows.length === 0 ? (
                        <div
                          className="rounded-xl border border-dashed border-[#E8E4E0] bg-background text-center text-[#888888]"
                          style={{
                            padding: `${24 * scale}px ${16 * scale}px`,
                            fontSize: `${14 * scale}px`,
                            lineHeight: `${20 * scale}px`,
                          }}
                        >
                          暂无可选升级方案
                        </div>
                      ) : (
                        displayRows.map(({ item, previewOption }, index) => {
                          const {
                            label,
                            price,
                            priceDiscountedPerMonth,
                            priceOriginalPerMonth,
                            desc,
                            badge,
                          } = item;
                          const showBadge = ['normal', 'primary', 'hyper'].includes(badge as string);
                          const selected = index === activedIndex;
                          const showPlus3xSelectedBadge =
                            paymentTabType === 'upgradePlus3x'
                            && isCurrentPlusUser
                            && selected;
                          /** Plus 会员：与 Tab 激活色 #A3866A 一致；Plus 3倍：#997F62（稿图二） */
                          const isPlusMemberTab = paymentTabType === 'upgrade';
                          const selectedBorder = isPlusMemberTab ? '#A3866A' : '#997F62';
                          const priceAccent = selectedBorder;
                          const showMonthlyPriceRow = !sameMonthlyUnitPrice(
                            priceDiscountedPerMonth,
                            priceOriginalPerMonth,
                          );
                          const outerR = PAYMENT_UPGRADE_CARD_OUTER_RADIUS_PX * scale;
                          const borderW = PAYMENT_UPGRADE_CARD_BORDER_PX * scale;
                          const innerR = paymentUpgradeCardInnerRadiusPx(scale);
                          const selectedShadow = isPlusMemberTab
                            ? '0 2px 12px rgba(163, 134, 106, 0.16)'
                            : '0 2px 12px rgba(153, 127, 98, 0.14)';
                          const badgeInset = selected || hoveredCardIndex === index;
                          return (
                            <div
                              key={`${label}-${price}-${index}`}
                              className={cn(
                                'relative box-border flex min-h-0 w-full cursor-pointer select-none flex-col border-solid bg-background transition-colors',
                                !selected && 'border-transparent hover:border-[#D4CEC6]',
                              )}
                              style={{
                                minHeight: `${PAYMENT_OPTION_CARD_MIN_HEIGHT_PX * scale}px`,
                                boxSizing: 'border-box',
                                borderWidth: borderW,
                                ...(selected ? { borderColor: selectedBorder } : {}),
                                borderRadius: `${outerR}px`,
                                boxShadow: selected ? selectedShadow : undefined,
                                padding: 0,
                              }}
                              onClick={() => {
                                setActivedIndex(index);
                              }}
                              onMouseEnter={() => setHoveredCardIndex(index)}
                              onMouseLeave={() => setHoveredCardIndex(null)}
                            >
                              {showPlus3xSelectedBadge ? (
                                <div
                                  className="absolute right-0 top-0 z-20 flex flex-row items-center justify-center text-background font-semibold"
                                  style={{
                                    borderRadius: `0 ${innerR}px 0 ${innerR}px`,
                                    background: '#997F62',
                                    fontSize: `${12 * scale}px`,
                                    lineHeight: `${16 * scale}px`,
                                    height: `${PAYMENT_UPGRADE_DISCOUNT_BADGE_INSET_HEIGHT_PX * scale}px`,
                                    minHeight: `${PAYMENT_UPGRADE_DISCOUNT_BADGE_INSET_HEIGHT_PX * scale}px`,
                                    paddingLeft: `${10 * scale}px`,
                                    paddingRight: `${10 * scale}px`,
                                    gap: `${4 * scale}px`,
                                  }}
                                >
                                  <UpgradeArrowIcon
                                    size={Math.round(16 * scale)}
                                    className="shrink-0"
                                    aria-hidden
                                  />
                                  升级
                                </div>
                              ) : null}
                              {showBadge && (
                                <div
                                  className="absolute z-10"
                                  style={{
                                    left: badgeInset ? 0 : `${-3 * scale}px`,
                                    top: badgeInset ? 0 : `${-3 * scale}px`,
                                  }}
                                >
                                  <PriceBadge
                                    label={desc}
                                    badge={badge as BadgeType}
                                    scale={scale}
                                    corner="tl-br"
                                    alignCornerRadiusPx={
                                      badgeInset
                                        ? PAYMENT_UPGRADE_CARD_INNER_RADIUS_PX
                                        : PAYMENT_UPGRADE_CARD_OUTER_RADIUS_PX
                                    }
                                    upgradeTlMode={badgeInset ? 'inset' : 'offset'}
                                  />
                                </div>
                              )}
                              <div
                                className="flex min-h-0 min-w-0 flex-1 flex-row items-center justify-between gap-3"
                                style={{
                                  paddingLeft: `${44 * scale}px`,
                                  paddingRight: `${58 * scale}px`,
                                  paddingTop: `${12 * scale}px`,
                                  paddingBottom: `${12 * scale}px`,
                                }}
                              >
                                <div
                                  className="shrink-0 text-[#3D3D3D]"
                                  style={{
                                    fontSize: `${32 * scale}px`,
                                    lineHeight: `${40 * scale}px`,
                                    fontWeight: 600,
                                  }}
                                >
                                  {label}
                                </div>
                                <div
                                  className="flex min-w-0 flex-col items-start justify-center gap-0.5 text-left"
                                  style={{ minWidth: `${120 * scale}px` }}
                                >
                                  <MemberLabel
                                    className="flex flex-row flex-nowrap items-baseline justify-start proportional-nums"
                                    style={{ gap: `${4 * scale}px` }}
                                  >
                                    <span
                                      style={{
                                        fontSize: `${22 * scale}px`,
                                        lineHeight: `${32 * scale}px`,
                                        fontWeight: 600,
                                      }}
                                    >¥</span>
                                    <span
                                      style={{
                                        fontSize: `${36 * scale}px`,
                                        lineHeight: `${40 * scale}px`,
                                        fontWeight: 600,
                                      }}
                                    >{price}</span>
                                  </MemberLabel>
                                  {showMonthlyPriceRow ? (
                                    <div
                                      className="inline-flex max-w-full flex-wrap justify-start gap-x-1 text-annotation-foreground"
                                      style={{
                                        fontSize: `${16 * scale}px`,
                                        lineHeight: `${22 * scale}px`,
                                        fontWeight: 400,
                                      }}
                                    >
                                      <span>¥{priceDiscountedPerMonth}/月</span>
                                      <span>
                                        <span className="line-through">¥{priceOriginalPerMonth}</span>/月
                                      </span>
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )
                    }
                  </div>
                  <div
                    className="text-annotation-foreground"
                    style={{
                      fontSize: `${12 * scale}px`,
                      lineHeight: `${18 * scale}px`,
                      marginTop: `${4 * scale}px`,
                    }}
                  >
                    {durationNote}
                  </div>
                </div>
              </div>
              <div
                className="flex min-w-0 flex-col overflow-x-hidden overflow-y-hidden"
                style={{ flex: '539 1 0', minWidth: 0 }}
              >
                <PaymentBenefitsCompareTable scale={scale} variant={compareVariant} className="w-full max-w-full" />
              </div>
            </div>
          </div>
        </div>
        {leftColumnFooter ? (
          <div
            ref={upgradeFooterRef}
            className="absolute bottom-0 left-0 right-0 z-10 w-full h-fit bg-transparent pointer-events-none"
            style={{
              paddingLeft: `${32 * scale}px`,
              paddingRight: `${32 * scale}px`,
              paddingTop: `${12 * scale}px`,
              paddingBottom: 0,
            }}
          >
            {leftColumnFooter}
          </div>
        ) : null}
      </div>
    </div>
  );
};
